import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Shield, Bell, CheckCircle } from 'lucide-react';

const SolutionSection = () => {
  const features = [
    {
      icon: FileText,
      title: 'Instant Translation',
      description: 'We translate complex legal jargon into plain, simple English that any parent can understand in seconds.',
      highlights: ['7,000 words → 3 bullet points', 'Reading level: 6th grade', 'Key risks highlighted'],
      image: 'https://customer-assets.emergentagent.com/job_protect-plainly/artifacts/ihpzwzie_instant-translation.jpg',
      imageAlt: 'SaidSimply plain language translation of terms of service',
    },
    {
      icon: Shield,
      title: 'Family Risk Score',
      description: 'Every policy is scored against your custom family profile, because what matters to your family is unique.',
      highlights: ['Customizable risk factors', 'Age-appropriate analysis', 'Privacy preference matching'],
      image: 'https://customer-assets.emergentagent.com/job_protect-plainly/artifacts/skm9dmfd_saidsimply-family-risk-score.jpg',
      imageAlt: 'Legal scoring breakdown showing data privacy, content ownership, and liability scores',
    },
    {
      icon: Bell,
      title: '24/7 Policy Watchdog',
      description: 'We monitor every agreement your family has accepted and alert you instantly when anything changes.',
      highlights: ['Real-time monitoring', 'Change summaries in plain English', 'Action recommendations'],
      image: 'https://customer-assets.emergentagent.com/job_protect-plainly/artifacts/69vlv6hz_saidsimply-watchdog.jpg',
      imageAlt: 'Your Risk Profile settings with personal details and risk tolerance options',
    },
  ];

  return (
    <section className="py-24 md:py-32 bg-[#FDFBF7]" id="features" data-testid="solution-section">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="text-[#0D9488] font-medium text-sm uppercase tracking-wider">The Solution</span>
          <h2 
            className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-[#292524] mt-4"
            data-testid="solution-heading"
          >
            Peace of Mind, Automated
          </h2>
          <p className="mt-6 text-lg text-[#78716C] max-w-2xl mx-auto">
            SaidSimply™ does the reading for you, so you can focus on what matters — your family.
          </p>
        </motion.div>

        <div className="space-y-24">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              className={`grid grid-cols-1 md:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'md:flex-row-reverse' : ''
              }`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              data-testid={`feature-${index}`}
            >
              <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                <div className="w-16 h-16 rounded-2xl bg-[#0D9488]/10 flex items-center justify-center mb-6">
                  <feature.icon className="w-8 h-8 text-[#0D9488]" />
                </div>
                <h3 className="font-heading text-2xl md:text-3xl font-medium text-[#292524] mb-4">
                  {feature.title}
                </h3>
                <p className="text-lg text-[#78716C] mb-6 leading-relaxed">
                  {feature.description}
                </p>
                <ul className="space-y-3">
                  {feature.highlights.map((highlight, hIndex) => (
                    <li key={hIndex} className="flex items-center gap-3 text-[#292524]">
                      <CheckCircle className="w-5 h-5 text-[#0D9488] flex-shrink-0" />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                <div className="relative">
                  <div className="absolute -inset-4 bg-[#0D9488]/5 rounded-3xl blur-2xl"></div>
                  <img
                    src={feature.image}
                    alt={feature.imageAlt}
                    className="relative rounded-2xl shadow-xl w-full object-cover border border-stone-100"
                    data-testid={`feature-image-${index}`}
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
